<template>
  <div class="space-y-6">
    <div>
      <h2 class="text-base font-semibold text-gray-900">Regions</h2>
      <p class="text-sm text-gray-500 mt-0.5">Define geographic region boundaries on the map</p>
    </div>

    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden" style="height: 700px">
      <div class="flex h-full">
        <!-- Sidebar -->
        <div class="w-72 flex-shrink-0 border-r border-gray-200 flex flex-col h-full overflow-hidden">
          <!-- Top action area -->
          <div class="p-4 border-b border-gray-200">
            <!-- New Region button -->
            <button
              v-if="!isDrawing && !editingRegion"
              @click="startDrawing()"
              class="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:opacity-90 transition-colors"
            >
              <i class="fa-solid fa-plus"></i>
              New Region
            </button>

            <!-- Drawing form -->
            <div v-if="isDrawing" class="space-y-3">
              <div class="flex items-center gap-2 text-sm font-medium text-gray-700">
                <i class="fa-solid fa-draw-polygon text-primary"></i>
                Draw Region
              </div>
              <input
                v-model="newRegionName"
                type="text"
                placeholder="Region name"
                class="w-full text-sm border border-gray-200 rounded-lg px-3 h-9 outline-none focus:border-indigo-400"
              />
              <div>
                <label class="text-xs font-medium text-gray-500 block mb-1.5">Color</label>
                <div class="flex gap-1.5 flex-wrap">
                  <button
                    v-for="c in colorPresets"
                    :key="c"
                    @click="newRegionColor = c"
                    class="w-7 h-7 rounded-full border-2 transition-all"
                    :style="{ backgroundColor: c }"
                    :class="newRegionColor === c ? 'border-gray-800 scale-110' : 'border-transparent'"
                  ></button>
                </div>
              </div>
              <p class="text-xs text-gray-400">Click on the map to draw polygon points. Double-click to finish.</p>
              <div class="flex gap-2">
                <button
                  @click="saveDrawnRegion()"
                  :disabled="!newRegionName.trim() || saving"
                  class="flex-1 px-3 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-40 transition-colors"
                >
                  <i v-if="saving" class="fa-solid fa-spinner fa-spin mr-1"></i>
                  Save
                </button>
                <button
                  @click="cancelDrawing()"
                  class="flex-1 px-3 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>

            <!-- Edit form -->
            <div v-if="editingRegion" class="space-y-3">
              <div class="flex items-center gap-2 text-sm font-medium text-gray-700">
                <i class="fa-solid fa-pen text-primary"></i>
                Edit Region
              </div>
              <input
                v-model="editRegionName"
                type="text"
                placeholder="Region name"
                class="w-full text-sm border border-gray-200 rounded-lg px-3 h-9 outline-none focus:border-indigo-400"
              />
              <div>
                <label class="text-xs font-medium text-gray-500 block mb-1.5">Color</label>
                <div class="flex gap-1.5 flex-wrap">
                  <button
                    v-for="c in colorPresets"
                    :key="c"
                    @click="editRegionColor = c"
                    class="w-7 h-7 rounded-full border-2 transition-all"
                    :style="{ backgroundColor: c }"
                    :class="editRegionColor === c ? 'border-gray-800 scale-110' : 'border-transparent'"
                  ></button>
                </div>
              </div>
              <div class="flex gap-2">
                <button
                  @click="saveEditRegion()"
                  :disabled="!editRegionName.trim() || saving"
                  class="flex-1 px-3 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-40 transition-colors"
                >
                  <i v-if="saving" class="fa-solid fa-spinner fa-spin mr-1"></i>
                  Save
                </button>
                <button
                  @click="cancelEdit()"
                  class="flex-1 px-3 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <!-- Region list (always visible) -->
          <div class="flex-1 overflow-y-auto">
            <div v-if="loading" class="p-6 text-center">
              <i class="fa-solid fa-spinner fa-spin text-gray-400 text-lg mb-2"></i>
              <p class="text-sm text-gray-400">Loading regions...</p>
            </div>

            <div v-else-if="regions.length === 0 && !isDrawing" class="p-6 text-center">
              <i class="fa-solid fa-map text-gray-300 text-2xl mb-2"></i>
              <p class="text-sm text-gray-400">No regions defined</p>
              <p class="text-xs text-gray-400 mt-1">Click "New Region" to get started</p>
            </div>

            <div v-if="regions.length > 0">
              <div
                v-for="region in regions"
                :key="region.id"
                @click="focusRegion(region)"
                class="flex items-center gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
                :class="{ 'bg-indigo-50': focusedRegionId === region.id }"
              >
                <span
                  class="w-3 h-3 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: region.color }"
                ></span>
                <span class="text-sm text-gray-700 flex-1 truncate">{{ region.name }}</span>
                <div class="flex gap-1">
                  <button
                    @click.stop="startEdit(region)"
                    class="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                    title="Edit"
                  >
                    <i class="fa-solid fa-pen text-xs"></i>
                  </button>
                  <button
                    @click.stop="confirmDelete(region)"
                    class="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Delete"
                  >
                    <i class="fa-solid fa-trash text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Map -->
        <div class="flex-1 relative">
          <div ref="mapContainer" class="w-full h-full"></div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div v-if="deleteTarget" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" @click.self="deleteTarget = null">
      <div class="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
        <h3 class="text-base font-semibold text-gray-900 mb-2">Delete Region</h3>
        <p class="text-sm text-gray-600 mb-5">
          Are you sure you want to delete <strong>{{ deleteTarget.name }}</strong>? This cannot be undone.
        </p>
        <div class="flex gap-3 justify-end">
          <button
            @click="deleteTarget = null"
            class="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="deleteRegion()"
            :disabled="deleting"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            <i v-if="deleting" class="fa-solid fa-spinner fa-spin mr-1"></i>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { db } from "../../firebase";
import { collection, getDocs, Timestamp } from "firebase/firestore";
import { postFire, setFire, deleteFire } from "../../firestore/utils";
import { polygonToCoords, coordsToPolygon } from "../../utils/regionUtils";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw";

// Custom draw styles compatible with mapbox-gl v2 (avoids line-dasharray expression bug)
const DRAW_STYLES = [
  // Polygon fill
  {
    id: "gl-draw-polygon-fill-inactive",
    type: "fill",
    filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
    paint: { "fill-color": "#3bb2d0", "fill-outline-color": "#3bb2d0", "fill-opacity": 0.1 },
  },
  {
    id: "gl-draw-polygon-fill-active",
    type: "fill",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
    paint: { "fill-color": "#fbb03b", "fill-outline-color": "#fbb03b", "fill-opacity": 0.1 },
  },
  // Polygon outline
  {
    id: "gl-draw-polygon-stroke-inactive",
    type: "line",
    filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": "#3bb2d0", "line-width": 2 },
  },
  {
    id: "gl-draw-polygon-stroke-active",
    type: "line",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": "#fbb03b", "line-width": 2 },
  },
  // Line
  {
    id: "gl-draw-line-inactive",
    type: "line",
    filter: ["all", ["==", "active", "false"], ["==", "$type", "LineString"], ["!=", "mode", "static"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": "#3bb2d0", "line-width": 2 },
  },
  {
    id: "gl-draw-line-active",
    type: "line",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "LineString"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": "#fbb03b", "line-width": 2 },
  },
  // Vertices
  {
    id: "gl-draw-polygon-and-line-vertex-inactive",
    type: "circle",
    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
    paint: { "circle-radius": 5, "circle-color": "#fff", "circle-stroke-color": "#3bb2d0", "circle-stroke-width": 2 },
  },
  {
    id: "gl-draw-polygon-and-line-vertex-active",
    type: "circle",
    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
    paint: { "circle-radius": 7, "circle-color": "#fff", "circle-stroke-color": "#fbb03b", "circle-stroke-width": 2 },
  },
  // Midpoints
  {
    id: "gl-draw-polygon-midpoint",
    type: "circle",
    filter: ["all", ["==", "meta", "midpoint"], ["==", "$type", "Point"]],
    paint: { "circle-radius": 3, "circle-color": "#fbb03b" },
  },
  // Points
  {
    id: "gl-draw-point-inactive",
    type: "circle",
    filter: ["all", ["==", "active", "false"], ["==", "meta", "feature"], ["==", "$type", "Point"]],
    paint: { "circle-radius": 5, "circle-color": "#3bb2d0" },
  },
  {
    id: "gl-draw-point-active",
    type: "circle",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "Point"]],
    paint: { "circle-radius": 7, "circle-color": "#fbb03b" },
  },
];

export default {
  data() {
    return {
      loading: false,
      saving: false,
      regions: [],
      map: null,
      draw: null,

      // Drawing state
      isDrawing: false,
      newRegionName: "",
      newRegionColor: "#3B82F6",

      // Edit state
      editingRegion: null,
      editRegionName: "",
      editRegionColor: "",
      focusedRegionId: null,

      // Delete state
      deleteTarget: null,
      deleting: false,

      // Track source IDs we've added so cleanup works for stale ones
      renderedRegionIds: [],

      colorPresets: [
        "#3B82F6", "#EF4444", "#10B981", "#F59E0B",
        "#8B5CF6", "#EC4899", "#06B6D4", "#F97316",
        "#6366F1", "#14B8A6", "#84CC16", "#D946EF",
      ],
    };
  },

  methods: {
    initMap() {
      mapboxgl.accessToken = MAPBOX_TOKEN;
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: "mapbox://styles/mapbox/light-v11",
        center: [153.2278, -27.7305],
        zoom: 9,
      });

      this.map.addControl(new mapboxgl.NavigationControl(), "top-right");

      this.map.on("load", () => {
        this.renderRegionsOnMap();
      });
    },

    renderRegionsOnMap() {
      if (!this.map || !this.map.isStyleLoaded()) return;

      // Remove previously rendered layers/sources
      this.renderedRegionIds.forEach((id) => {
        const fillId = `region-fill-${id}`;
        const lineId = `region-line-${id}`;
        const labelId = `region-label-${id}`;
        const srcId = `region-src-${id}`;

        if (this.map.getLayer(fillId)) this.map.removeLayer(fillId);
        if (this.map.getLayer(lineId)) this.map.removeLayer(lineId);
        if (this.map.getLayer(labelId)) this.map.removeLayer(labelId);
        if (this.map.getSource(srcId)) this.map.removeSource(srcId);
      });
      this.renderedRegionIds = [];

      // Add all regions
      this.regions.forEach((r) => {
        if (!r.polygon || r.polygon.length < 3) return;

        const srcId = `region-src-${r.id}`;
        const fillId = `region-fill-${r.id}`;
        const lineId = `region-line-${r.id}`;
        const labelId = `region-label-${r.id}`;

        // Convert from Firestore format to GeoJSON coords
        const coords = polygonToCoords(r.polygon);
        // Ensure the ring is closed
        const first = coords[0];
        const last = coords[coords.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
          coords.push([...first]);
        }

        const centroid = this.computeCentroid(coords);

        this.map.addSource(srcId, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: { type: "Polygon", coordinates: [coords] },
                properties: { name: r.name },
              },
              {
                type: "Feature",
                geometry: { type: "Point", coordinates: centroid },
                properties: { name: r.name },
              },
            ],
          },
        });

        this.map.addLayer({
          id: fillId,
          type: "fill",
          source: srcId,
          filter: ["==", "$type", "Polygon"],
          paint: {
            "fill-color": r.color,
            "fill-opacity": 0.15,
          },
        });

        this.map.addLayer({
          id: lineId,
          type: "line",
          source: srcId,
          filter: ["==", "$type", "Polygon"],
          paint: {
            "line-color": r.color,
            "line-width": 2,
          },
        });

        this.map.addLayer({
          id: labelId,
          type: "symbol",
          source: srcId,
          filter: ["==", "$type", "Point"],
          layout: {
            "text-field": ["get", "name"],
            "text-size": 13,
            "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
            "text-allow-overlap": true,
          },
          paint: {
            "text-color": r.color,
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.5,
          },
        });

        this.renderedRegionIds.push(r.id);
      });
    },

    computeCentroid(coords) {
      const ring = coords[0][0] === coords[coords.length - 1][0] &&
                   coords[0][1] === coords[coords.length - 1][1]
        ? coords.slice(0, -1)
        : coords;
      let sumLng = 0, sumLat = 0;
      ring.forEach(([lng, lat]) => { sumLng += lng; sumLat += lat; });
      return [sumLng / ring.length, sumLat / ring.length];
    },

    async fetchRegions() {
      this.loading = true;
      try {
        const snap = await getDocs(collection(db, "regions"));
        const list = [];
        snap.forEach((d) => {
          list.push({ ...d.data(), id: d.id });
        });
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        this.regions = list;
        this.renderRegionsOnMap();
      } catch (e) {
        console.error("Failed to load regions:", e);
      }
      this.loading = false;
    },

    // Drawing
    startDrawing() {
      this.isDrawing = true;
      this.newRegionName = "";
      this.newRegionColor = "#3B82F6";
      this.focusedRegionId = null;

      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: { polygon: true, trash: true },
        defaultMode: "draw_polygon",
        styles: DRAW_STYLES,
      });
      this.map.addControl(this.draw);
    },

    removeDrawControl() {
      if (this.draw) {
        this.map.removeControl(this.draw);
        this.draw = null;
      }
    },

    cancelDrawing() {
      this.removeDrawControl();
      this.isDrawing = false;
      this.newRegionName = "";
      this.newRegionColor = "#3B82F6";
    },

    async saveDrawnRegion() {
      if (!this.newRegionName.trim()) return;

      // Pull polygon directly from the draw instance
      const allFeatures = this.draw ? this.draw.getAll() : null;
      if (!allFeatures || !allFeatures.features.length) {
        alert("Please draw a polygon on the map first.");
        return;
      }

      const rawCoords = allFeatures.features[0].geometry.coordinates[0];
      if (!rawCoords || rawCoords.length < 4) {
        alert("Please draw a polygon with at least 3 points.");
        return;
      }

      // Convert nested arrays to Firestore-safe {lng, lat} objects
      const polygon = coordsToPolygon(rawCoords);

      this.saving = true;
      const body = {
        name: this.newRegionName.trim(),
        color: this.newRegionColor,
        polygon: polygon,
        created: Timestamp.now(),
        updated: Timestamp.now(),
      };

      try {
        const result = await postFire({ collection: "regions", body });
        this.regions.push({ ...body, id: result.id });
        this.regions.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        this.removeDrawControl();
        this.isDrawing = false;
        this.newRegionName = "";
        this.newRegionColor = "#3B82F6";
        this.renderRegionsOnMap();
        this.focusRegion({ ...body, id: result.id });
      } catch (e) {
        console.error("Failed to save region:", e);
      }
      this.saving = false;
    },

    // Edit
    startEdit(region) {
      if (this.isDrawing) this.cancelDrawing();
      this.editingRegion = region;
      this.editRegionName = region.name;
      this.editRegionColor = region.color;
      this.focusRegion(region);
    },

    cancelEdit() {
      this.editingRegion = null;
      this.editRegionName = "";
      this.editRegionColor = "";
    },

    async saveEditRegion() {
      if (!this.editRegionName.trim() || !this.editingRegion) return;

      this.saving = true;
      const body = {
        name: this.editRegionName.trim(),
        color: this.editRegionColor,
        updated: Timestamp.now(),
      };

      try {
        await setFire({
          collection: "regions",
          doc: this.editingRegion.id,
          body,
        });

        const idx = this.regions.findIndex((r) => r.id === this.editingRegion.id);
        if (idx !== -1) {
          this.$set(this.regions, idx, { ...this.regions[idx], ...body });
        }
        this.regions.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        this.editingRegion = null;
        this.editRegionName = "";
        this.editRegionColor = "";
        this.renderRegionsOnMap();
      } catch (e) {
        console.error("Failed to update region:", e);
      }
      this.saving = false;
    },

    // Delete
    confirmDelete(region) {
      this.deleteTarget = region;
    },

    async deleteRegion() {
      if (!this.deleteTarget) return;
      this.deleting = true;

      try {
        await deleteFire({ collection: "regions", doc: this.deleteTarget.id });

        const r = this.deleteTarget;
        const fillId = `region-fill-${r.id}`;
        const lineId = `region-line-${r.id}`;
        const labelId = `region-label-${r.id}`;
        const srcId = `region-src-${r.id}`;
        if (this.map.getLayer(fillId)) this.map.removeLayer(fillId);
        if (this.map.getLayer(lineId)) this.map.removeLayer(lineId);
        if (this.map.getLayer(labelId)) this.map.removeLayer(labelId);
        if (this.map.getSource(srcId)) this.map.removeSource(srcId);

        this.renderedRegionIds = this.renderedRegionIds.filter((id) => id !== r.id);
        this.regions = this.regions.filter((reg) => reg.id !== this.deleteTarget.id);
        if (this.focusedRegionId === this.deleteTarget.id) {
          this.focusedRegionId = null;
        }
        if (this.editingRegion && this.editingRegion.id === this.deleteTarget.id) {
          this.cancelEdit();
        }
        this.deleteTarget = null;
      } catch (e) {
        console.error("Failed to delete region:", e);
      }
      this.deleting = false;
    },

    // Focus / highlight
    focusRegion(region) {
      this.focusedRegionId = region.id;
      if (!region.polygon || region.polygon.length < 2) return;

      const coords = polygonToCoords(region.polygon);
      const bounds = new mapboxgl.LngLatBounds();
      coords.forEach(([lng, lat]) => {
        bounds.extend([lng, lat]);
      });
      this.map.fitBounds(bounds, { padding: 60, duration: 800 });
    },
  },

  mounted() {
    this.initMap();
    this.fetchRegions();
  },

  beforeDestroy() {
    this.removeDrawControl();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
};
</script>
